import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router';
import Loading from '../loading/Loading';
import { useAuth } from '../../context/auth-context';
import { AvailableDataset } from '../../types/decisions';
import { getAvailableDatasets } from '../../api/decisions.api';
import PatientStep, { Submenu } from '../PatientStep/PatientStep';
import { getPatientInfo, PatientTreatmentInfo } from '../../api/patient.api';
import './PatientSteps.scss';

type PatientStepData = {
    id: string;
    label: string;
    submenus?: Submenu[];
    url: string;
    baseUrl: string;
    isActive: boolean;
};

const DEFAULT_APP_ID = 667;
const DEFAULT_DATASET_ID = 'main';
const choiceData: {
    appId?: string;
    datasetId?: string;
    treatmentId?: string;
} = {};
const initialSteps: PatientStepData[] = [
    {
        id: 'treatments',
        label: 'Treatments',
        submenus: [
            {
                id: 'physicalTherapy',
                label: 'Physical therapy',
                url: '/contents/treatments/physical-therapy',
            },
            {
                id: 'nsaids',
                label: 'Nsaids',
                url: '/contents/treatments/nsaids',
            },
            {
                id: 'cortisoneInjection',
                label: 'Cortisone injection',
                url: '/contents/treatments/cortisone-injection',
            },
            {
                id: 'haInjection',
                label: 'HA injection',
                url: '/contents/treatments/ha-injection',
            },
            {
                id: 'prp',
                label: 'PRP',
                url: '/contents/treatments/prp',
            },
            {
                id: 'tka',
                label: 'TKA',
                url: '/contents/treatments/tka',
            },
        ],
        url: '/contents/treatments',
        baseUrl: '/contents/treatments',
        isActive: false,
    },
    {
        id: 'decisionFactors',
        label: 'Decision Factors',
        submenus: [
            {
                id: 'longTermSideEffects',
                label: 'Long-Term Side Effects/Risks',
                url: '/contents/decision-factors/long-term-side-effects',
            },
            {
                id: 'longTermObjectives',
                label: 'Long-Term Objectives',
                url: '/contents/decision-factors/long-term-objectives',
            },
            {
                id: 'treatmentSuccessRate',
                label: 'Treatment Success Rate',
                url: '/contents/decision-factors/treatment-success-rate',
            },
            {
                id: 'shortTermSideEffects',
                label: 'Short-Term Side Effects',
                url: '/contents/decision-factors/short-term-side-effects',
            },
            {
                id: 'timeCommitment',
                label: 'Time Commitment',
                url: '/contents/decision-factors/time-commitment',
            },
            {
                id: 'lifestyleImpactOfRecovery',
                label: 'Lifestyle Impact of Recovery',
                url: '/contents/decision-factors/lifestyle-impact-of-recovery',
            },
            {
                id: 'effortlessRecovery',
                label: 'Effortless Recovery',
                url: '/contents/decision-factors/effortless-recovery',
            },
            {
                id: 'painAvoidance',
                label: 'Pain Avoidance',
                url: '/contents/decision-factors/pain-avoidance',
            },
            {
                id: 'nonInvasivePreference',
                label: 'Non-Invasive Preference',
                url: '/contents/decision-factors/non-invasive-preference',
            },
            {
                id: 'outOfPocketCost',
                label: 'Out-of-Pocket Cost',
                url: '/contents/decision-factors/out-of-pocket-cost',
            },
        ],
        url: '/contents/decision-factors',
        baseUrl: '/contents/decision-factors',
        isActive: false,
    },
    {
        id: 'testYourKnowledge',
        label: 'Test your Knowledge',
        url: '/test-your-knowledge',
        baseUrl: '/test-your-knowledge',
        isActive: false,
    },
    {
        id: 'decide',
        label: 'Decide',
        submenus: [],
        url: '/apps',
        baseUrl: '/apps',
        isActive: false,
    },
    {
        id: 'myChoice',
        label: 'My Choice',
        url: '/patient-choice',
        baseUrl: '/patient-choice',
        isActive: false,
    },
];

const PatientSteps = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [ searchParams ] = useSearchParams();
    const { isAuthenticated, userInfo: authUserInfo } = useAuth();
    const [ steps, setSteps ] = useState<PatientStepData[]>(initialSteps);
    const [ availableDatasets, setAvailableDatasets ] = useState<AvailableDataset[]>([]);
    const [ availableDatasetsLoading, setAvailableDatasetsLoading ] = useState<boolean>(true);
    const [ , setUserInfo ] = useState<PatientTreatmentInfo[]>([]);
    const [ userInfoLoading, setUserInfoLoading ] = useState<boolean>(true);

    useEffect(() => {
        if (!isAuthenticated || !authUserInfo) {
            return;
        }

        getAvailableDatasets()
        .then((response) => {
            setAvailableDatasets(response);
            // user has no available datasets
            if (response.length === 0 && authUserInfo.role === 'patient') {
                return navigate('/screening-questions');
            }

            setSteps(steps => steps.map(step => {
                if ([ 'treatments', 'decisionFactors', 'testYourKnowledge', 'decide' ].includes(step.id)) {
                    return {
                        ...step,
                        isActive: true,
                    };
                }

                return step;
            }));
        })
        .catch((e) => {
            console.error(e);
        })
        .finally(() => setAvailableDatasetsLoading(false));

        if (authUserInfo.role === 'patient') {
            getPatientInfo()
            .then((response) => {
                setUserInfo(response);

                if (response.length === 0) {
                    return;
                }

                const { appId, datasetId, chosen, treatmentId } = response[0];
                if (!appId || !datasetId || !chosen || !treatmentId) {
                    return;
                }

                setSteps(steps => steps.map(step => {
                    if (step.id === 'myChoice') {
                        return {
                            ...step,
                            isActive: true,
                            url: `/patient-choice/${appId}?dataset=${datasetId}`,
                        };
                    }

                    return step;
                }));
            })
            .finally(() => setUserInfoLoading(false));
        } else if ([ 'doctor', 'manager' ].includes(authUserInfo.role)) {
            if (!choiceData.appId) {
                const match = location.pathname.match(/\/patient-choice\/(\d+)/);
                choiceData.appId = match ? match[1] : DEFAULT_APP_ID + '';
                choiceData.datasetId = searchParams.get('dataset') || 'main';
                choiceData.treatmentId = searchParams.get('treatmentId') || '0';
            }

            setSteps(steps => steps.map(step => {
                if (step.id === 'myChoice') {
                    return {
                        ...step,
                        isActive: true,
                        url: `/patient-choice/${choiceData.appId}?dataset=${choiceData.datasetId}&treatmentId=${choiceData.treatmentId}`,
                    };
                }

                return step;
            }));

            setUserInfoLoading(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ isAuthenticated, navigate, authUserInfo ]);

    const isStepActive = (menu: {
        baseUrl: string
    }): boolean => {
        return (
            location.pathname
        ).startsWith(menu.baseUrl);
    };

    const onStepClick = (menu: PatientStepData) => {
        if (menu.id === 'decide') {
            const { appId, datasetIds } = availableDatasets?.[0] || {
                appId: DEFAULT_APP_ID, datasetIds: [ DEFAULT_DATASET_ID ],
            };

            const stepURL = `/apps/${appId}?dataset=${datasetIds}`;
            return navigate(stepURL);
        }

        if (menu.url && !location.pathname.startsWith(menu.url)) {
            navigate(menu.url);
        }
    };

    const onItemClick = (item: Submenu) => {
        navigate(item.url);
    };

    if (availableDatasetsLoading || userInfoLoading) {
        return <Loading/>;
    }

    return (
        <div className="patient-steps">
            <div className="patient-steps_container">
                {steps.map((step, idx) => (
                    <PatientStep
                        key={idx}
                        index={idx + 1}
                        isActive={isStepActive(step)}
                        isDisabled={!step.isActive}
                        label={step.label}
                        onClick={() => onStepClick(step)}
                        onSubmenuItemClick={onItemClick}
                        submenuItems={step.submenus || []}
                    />
                ))}
            </div>
        </div>
    );
};

export default PatientSteps;